import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'

import T from 'T'
import { get } from 'utils/lodash'
import { getPaymentMethodImage, getPaymentMethodName } from 'utils/payment'
import { memo } from 'utils/react'
import { getSavedMethodsByPaymentType } from 'utils/takePayments'

// Renders the brand badge + label (+ expiry when shown). Used for both the collapsed select value
// and the dropdown options so they look identical.
const renderMethodRow = (method, { showExpiry = true } = {}) => {
  const accountType = get(method, 'accountType', '')
  const creditCardType = get(method, 'creditCardType')
  const methodName = getPaymentMethodName(creditCardType, accountType)
  const cardImage = getPaymentMethodImage(creditCardType || methodName)
  const expiry = get(method, 'expiry', '')
  const accountNumber = get(method, 'accountNumber', '')
  const cardNickName = get(method, 'cardNickName', '')
  const label = cardNickName || `${creditCardType || accountType} •••• ${accountNumber}`

  return (
    <Box alignItems="center" display="flex" gap={1.5} justifyContent="space-between" width="100%">
      <Box alignItems="center" display="flex" gap={1} minWidth={0}>
        <Box alt="" component="img" src={cardImage} sx={{ height: 20, flexShrink: 0 }} />
        <Typography noWrap>{label}</Typography>
      </Box>
      {showExpiry && expiry && expiry.length === 4 && (
        <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="caption">
          {`Expires ${expiry.substring(0, 2)}/${expiry.substring(2)}`}
        </Typography>
      )}
    </Box>
  )
}

const CreditCardAchListing = ({ type, selectedPaymentTokenDetails = {}, paymentMethods = [], onPaymentMethodSelect }) => {
  const paymentMethodByType = getSavedMethodsByPaymentType(paymentMethods, type)
  const selectedPaymentToken = get(selectedPaymentTokenDetails, 'accountToken', '')

  const handleChange = useCallback(
    event => {
      const token = event.target.value
      const method = paymentMethodByType.find(m => get(m, 'accountToken') === token)
      if (method) onPaymentMethodSelect(method)
    },
    [paymentMethodByType, onPaymentMethodSelect]
  )

  const renderSelectedValue = useCallback(
    token => {
      const method = paymentMethodByType.find(m => get(m, 'accountToken') === token)
      return method ? renderMethodRow(method, { showExpiry: false }) : null
    },
    [paymentMethodByType]
  )

  if (paymentMethodByType.length === 0) return null

  // If the current token is no longer in the saved-methods list (e.g. operator switched to the
  // "New card" radio), fall back to empty so the Select renders an unselected state instead of
  // warning about an out-of-range value.
  const safeValue = paymentMethodByType.some(method => get(method, 'accountToken') === selectedPaymentToken) ? selectedPaymentToken : ''

  return (
    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
      <InputLabel id="saved-payment-method-label">{T.SAVED_PAYMENT_METHOD}</InputLabel>
      <Select
        label={T.SAVED_PAYMENT_METHOD}
        labelId="saved-payment-method-label"
        renderValue={renderSelectedValue}
        value={safeValue}
        onChange={handleChange}
      >
        {paymentMethodByType.map(method => (
          <MenuItem key={get(method, 'accountToken')} value={get(method, 'accountToken')}>
            {renderMethodRow(method)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

CreditCardAchListing.propTypes = {
  type: PropTypes.string.isRequired,
  paymentMethods: PropTypes.array,
  selectedPaymentTokenDetails: PropTypes.object,
  onPaymentMethodSelect: PropTypes.func.isRequired,
}

export default memo(CreditCardAchListing)
